/**
 * ตรรกะล้วนของหน้าจองานจัดซื้อ — ใบขอซื้อ (D-084) ครึ่งบน และใบสั่งซื้อ (D-085)
 * ครึ่งล่าง แยกออกจาก JSX เพื่อให้ทดสอบกฎได้ตรง ๆ
 *
 * แนวเดียวกับ `lib/suppliers/suppliersView.ts`: สิ่งที่อยู่ที่นี่คือคำถามที่ตอบได้โดย
 * ไม่ต้องเรนเดอร์อะไรเลย — โดยเฉพาะสองข้อที่ผิดแล้วแพงที่สุดของกลุ่มนี้
 *
 * 1. **แถวนี้มีปุ่มอะไรได้บ้าง** (`lifecycleActionsFor`) ซึ่งเป็นการอ่าน state machine
 *    ของ contract ออกมาเป็นคำตอบเดียว ไม่ใช่เงื่อนไข `if` ที่กระจายอยู่ใน JSX
 * 2. **การประกอบ body** ซึ่งเป็นด่านเดียวที่ทางเขียนของหน้าจอนี้เดินผ่าน
 *
 * ⚠️ ไฟล์นี้ **ไม่มีฟังก์ชันที่ประกอบ `status`, `pr_id` หรือฟิลด์ผู้กระทำเลย** และ
 * นั่นเป็นข้อจำกัดโดยเจตนา: ทั้งหมดเป็นของเซิร์ฟเวอร์ (D-084) สถานะเปลี่ยนได้ทาง
 * เดียวคือ transition และผู้สร้างคือค่าที่ D-010 เอาไปเทียบกับผู้อนุมัติ
 */

import type {
  PurchaseOrderTransition,
  PurchaseRequisitionTransition,
} from './client';
import {
  ACKNOWLEDGED_PO_STATUS,
  APPROVED_PO_STATUS,
  APPROVED_PR_STATUS,
  CANCELLED_PO_STATUS,
  CANCELLED_PR_STATUS,
  CLOSED_PO_STATUS,
  CONVERTED_TO_PO_PR_STATUS,
  DRAFT_PO_STATUS,
  DRAFT_PR_STATUS,
  PARTIALLY_RECEIVED_PO_STATUS,
  PR_MAX_ITEMS_PER_SHEET,
  PROCUREMENT_MODULE,
  SENT_PO_STATUS,
  SUBMITTED_PR_STATUS,
  type CreatePurchaseOrderBody,
  type CreatePurchaseOrderLine,
  type PurchaseOrder,
  type CreatePurchaseRequisitionBody,
  type CreatePurchaseRequisitionHeaderBody,
  type CreatePurchaseRequisitionItemBody,
  type PurchaseRequisition,
} from './types';
import { can } from '../permissions/client';
import type { PermissionsState } from '../permissions/types';

export interface PurchaseRequisitionStatusDescriptor {
  readonly code: string;
  readonly label: string;
  readonly summary: string;
}

/**
 * ห้าสถานะของ lifecycle ตามลำดับที่ contract เขียนไว้
 *
 * ข้อความของแต่ละสถานะต้องบอกว่ามัน **ต่างกันอย่างไร** ไม่ใช่แค่แปลชื่อ — โดยเฉพาะ
 * สามสถานะปลายทางซึ่งอ่านเผิน ๆ เหมือน "จบแล้ว" เท่ากัน แต่ `APPROVED` คือใบที่รอ
 * ฝ่ายจัดซื้อออกใบสั่งซื้อ `CONVERTED_TO_PO` คือใบที่มีใบสั่งซื้อครอบแล้ว ส่วน
 * `CANCELLED` คือความต้องการที่ถูกถอนไป
 */
export const PURCHASE_REQUISITION_STATUS_DESCRIPTORS: readonly PurchaseRequisitionStatusDescriptor[] =
  [
    {
      code: DRAFT_PR_STATUS,
      label: 'ร่าง (DRAFT)',
      summary: 'เปิดไว้แล้วแต่ยังไม่ส่งให้ใคร — ยังไม่มีใครนอกจากผู้เปิดเห็นว่าต้องซื้อ',
    },
    {
      code: SUBMITTED_PR_STATUS,
      label: 'ส่งแล้ว รออนุมัติ (SUBMITTED)',
      summary: 'ถึงมือฝ่ายจัดซื้อแล้ว รอผู้อนุมัติซึ่งต้องไม่ใช่ผู้เปิดใบ (D-010)',
    },
    {
      code: APPROVED_PR_STATUS,
      label: 'อนุมัติแล้ว (APPROVED)',
      summary: 'ซื้อได้แล้ว — ยกเลิกที่หน้าจอนี้ไม่ได้อีก และรอถูกรวมเข้าใบสั่งซื้อ',
    },
    {
      code: CONVERTED_TO_PO_PR_STATUS,
      label: 'ออกใบสั่งซื้อแล้ว (CONVERTED_TO_PO)',
      summary:
        'มีใบสั่งซื้อครอบความต้องการนี้แล้ว — สถานะนี้เขียนโดยงานใบสั่งซื้อ (P2-2b) ไม่ใช่ที่นี่',
    },
    {
      code: CANCELLED_PR_STATUS,
      label: 'ยกเลิกแล้ว (CANCELLED)',
      summary: 'ถอนความต้องการก่อนอนุมัติ — ใบยังอ่านได้ตลอดไป ไม่ใช่การลบแถวทิ้ง',
    },
  ];

/** สีของป้ายสถานะ — บอก "ต้องทำอะไรต่อ" ไม่ใช่บอกว่าดีหรือแย่ */
export type PurchaseRequisitionStatusTone = 'neutral' | 'warning' | 'success' | 'info';

export function purchaseRequisitionStatusTone(
  status: string,
): PurchaseRequisitionStatusTone {
  switch (status) {
    case SUBMITTED_PR_STATUS:
      // สีเตือนเพราะมันคือใบที่ **รอคนกด** ไม่ใช่เพราะมีอะไรผิด
      return 'warning';
    case APPROVED_PR_STATUS:
      return 'success';
    case CONVERTED_TO_PO_PR_STATUS:
      return 'info';
    default:
      return 'neutral';
  }
}

/** ชื่อที่อ่านออกของสถานะ — **คืนค่าดิบเมื่อไม่รู้จัก ไม่ใช่ซ่อนแถวนั้น** */
export function purchaseRequisitionStatusLabel(status: string): string {
  return (
    PURCHASE_REQUISITION_STATUS_DESCRIPTORS.find((entry) => entry.code === status)
      ?.label ?? status
  );
}

/**
 * ใบในสถานะนี้มี transition อะไรให้กระทำได้บ้าง — **state machine ของ contract ทั้งอัน
 * อยู่ในฟังก์ชันเดียวนี้**
 *
 * `DRAFT` → ส่งอนุมัติ หรือ ยกเลิก
 * `SUBMITTED` → อนุมัติ หรือ ยกเลิก
 * `APPROVED` / `CONVERTED_TO_PO` / `CANCELLED` → **ไม่มีเลย** และสถานะที่หน้าจอยัง
 * ไม่รู้จักก็ไม่มีเช่นกัน (deny-safe: ปุ่มที่เดาว่าน่าจะกดได้ คือปุ่มที่จะถูกตอบ `409`)
 *
 * ⚠️ นี่คือ **UX layer** ไม่ใช่ด่านความปลอดภัย เซิร์ฟเวอร์ตรวจ source status ของทุก
 * คำขอเสมอ สิ่งที่ฟังก์ชันนี้ทำคือไม่เสนอปุ่มที่จะถูกปฏิเสธแน่ ๆ ให้ผู้ใช้กด
 */
export function lifecycleActionsFor(
  status: string,
): readonly PurchaseRequisitionTransition[] {
  switch (status) {
    case DRAFT_PR_STATUS:
      return ['submit', 'cancel'];
    case SUBMITTED_PR_STATUS:
      return ['approve', 'cancel'];
    default:
      return [];
  }
}

/** สิทธิ์ที่ปุ่มแต่ละปุ่มต้องการ — ตรงกับด่านจริงฝั่งเซิร์ฟเวอร์ทีละเส้น */
export function actionPermission(
  transition: PurchaseRequisitionTransition,
): 'EDIT' | 'APPROVE' {
  return transition === 'approve' ? 'APPROVE' : 'EDIT';
}

/** คำบนปุ่ม */
export function actionLabel(transition: PurchaseRequisitionTransition): string {
  switch (transition) {
    case 'submit':
      return 'ส่งอนุมัติ';
    case 'approve':
      return 'อนุมัติ';
    case 'cancel':
      return 'ยกเลิกใบ';
  }
}

/** สถานะที่ transition นั้นพาใบไปถึง — ใช้เขียนข้อความยืนยันผลที่อ่านออก */
export function actionTargetStatus(transition: PurchaseRequisitionTransition): string {
  switch (transition) {
    case 'submit':
      return SUBMITTED_PR_STATUS;
    case 'approve':
      return APPROVED_PR_STATUS;
    case 'cancel':
      return CANCELLED_PR_STATUS;
  }
}

/**
 * กรองรายการด้วยคำค้นเดียว — เทียบ **เลขใบและรหัสสินค้า**
 *
 * สองคอลัมน์นี้คือสิ่งที่คนถือใบอยู่ในมือจะคัดลอกมาค้น: เจ้าหน้าที่จัดซื้อค้นด้วย
 * เลขใบที่อ้างถึงกันในอีเมล ส่วนฝ่ายวางแผนค้นด้วยรหัสสินค้าที่ตัวเองขอไป
 *
 * กรอง **ฝั่งเบราว์เซอร์จากหน้าที่โหลดมาแล้ว** ไม่ยิงคำขอใหม่ — contract ของ
 * `GET /v1/purchase-requisitions` ไม่มีพารามิเตอร์ค้นหาเลย มีแต่
 * `plant_code`/`status`/`limit`/`cursor`
 */
export function filterPurchaseRequisitions(
  items: readonly PurchaseRequisition[],
  query: string,
): readonly PurchaseRequisition[] {
  const needle = query.trim().toLocaleLowerCase();
  if (needle === '') {
    return items;
  }
  return items.filter(
    (requisition) =>
      requisition.pr_number.toLocaleLowerCase().includes(needle) ||
      requisition.product_code.toLocaleLowerCase().includes(needle),
  );
}

/**
 * จำนวนที่กรอกใช้ได้หรือยัง — ตรวจ **เป็นสตริง ไม่แปลงเป็น number แม้แต่ครั้งเดียว**
 *
 * `NUMERIC(18, 4)` เก็บได้ถึง 14 หลักหน้าจุดและ 4 หลังจุด ซึ่งกว้างกว่าที่ IEEE-754
 * double เก็บได้ครบ การตรวจด้วย `Number(value) > 0` จะ "ผ่าน" ค่าที่ปัดไปแล้ว และ
 * ค่าที่ปัดแล้วคือจำนวนที่ผู้ใช้ไม่ได้สั่งซื้อ
 *
 * ศูนย์ (`0`, `0.0000`) ถูกปฏิเสธที่นี่ด้วย ทั้งที่เซิร์ฟเวอร์ก็ปฏิเสธเช่นกัน
 * (`400 QUANTITY_MUST_BE_POSITIVE`) — ปุ่มที่กดแล้วรู้ผลทันทีดีกว่าการเดินทางไป
 * กลับหนึ่งรอบเพื่อบอกสิ่งที่หน้าจอรู้อยู่แล้ว
 */
export function isPositiveQuantity(value: string): boolean {
  const trimmed = value.trim();
  if (!/^\d{1,14}(\.\d{1,4})?$/.test(trimmed)) {
    return false;
  }
  return /[1-9]/.test(trimmed);
}

/**
 * แปลงค่าจากช่อง `<input type="date">` (`YYYY-MM-DD`) เป็น ISO-8601 **ที่มี offset**
 *
 * ส่งสตริง `"2026-11-01"` ดิบ ๆ ไปไม่ได้: `datetime` ฝั่งเซิร์ฟเวอร์จะอ่านมันเป็น
 * เที่ยงคืน **โดยไม่มีเขตเวลา** แล้วเก็บเป็น UTC ซึ่งสำหรับโรงงานที่ `+07:00` คือ
 * เย็นของ **วันก่อนหน้า** — วันที่ต้องการของกลายเป็นคนละวันเงียบ ๆ
 *
 * ค่าที่คืนคือ **เที่ยงคืนตามเวลาเครื่องผู้ใช้** ในรูป instant ที่ระบุ offset ชัดเจน
 * (`toISOString()` ให้รูป `Z`) วันที่ที่ผู้ใช้เลือกจึงเดินทางกลับมาเป็นวันเดิมเสมอ
 * เมื่ออ่านด้วย `requiredDateLabel()`
 *
 * คืน `null` เมื่อรูปแบบไม่ใช่ `YYYY-MM-DD` หรือเป็นวันที่ที่ไม่มีอยู่จริง (เช่น
 * `2026-02-30` ซึ่ง `Date` จะเลื่อนให้เป็นต้นเดือนถัดไปโดยไม่บอกใคร)
 */
export function toRequiredDateIso(dateValue: string): string | null {
  const trimmed = dateValue.trim();
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (match === null) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  const local = new Date(year, month - 1, day, 0, 0, 0, 0);
  // `Date` รับ `2026-02-30` แล้วเลื่อนเป็น `2026-03-02` ให้เงียบ ๆ — เทียบกลับ
  // ทีละส่วนคือวิธีเดียวที่จะเห็นว่ามันเลื่อน
  if (
    local.getFullYear() !== year ||
    local.getMonth() !== month - 1 ||
    local.getDate() !== day
  ) {
    return null;
  }
  return local.toISOString();
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

/**
 * วันที่ต้องการของในรูป `YYYY-MM-DD` **ตามเวลาเครื่องผู้ใช้**
 *
 * คู่กับ `toRequiredDateIso()` — สิ่งที่ผู้ใช้เลือกในฟอร์มคือสิ่งที่เขาเห็นในตาราง
 *
 * ค่าที่อ่านไม่ออกถูกคืนตามเดิม **ไม่ใช่แสดงเป็นช่องว่างหรือ "Invalid Date"**:
 * วันที่ที่หายไปจากตารางอ่านเหมือน "ใบนี้ไม่ได้ระบุว่าต้องการเมื่อไร" ซึ่งเป็นไปไม่ได้
 * ตาม contract (คอลัมน์นี้ `NOT NULL`)
 */
export function requiredDateLabel(isoValue: string): string {
  const parsed = new Date(isoValue);
  if (Number.isNaN(parsed.getTime())) {
    return isoValue;
  }
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
}

/** ค่าที่ฟอร์มสร้างใบถืออยู่ — ดิบ ๆ ตามที่ผู้ใช้พิมพ์ ยังไม่ผ่านการ trim */
export interface CreatePurchaseRequisitionFields {
  readonly prNumber: string;
  readonly plantCode: string;
  readonly productCode: string;
  readonly quantity: string;
  readonly uomCode: string;
  /** ค่าจาก `<input type="date">` — `YYYY-MM-DD` */
  readonly requiredDate: string;
  readonly suggestedSupplierCode: string;
  readonly note: string;
}

/**
 * ประกอบ body ของ `POST /v1/purchase-requisitions` — คืน `null` เมื่อยังประกอบไม่ได้
 *
 * ทุกรหัสถูก `trim()` เพราะช่องว่างหน้า/หลังเป็นสิ่งที่มองไม่เห็นบนหน้าจอแต่ **ติดอยู่
 * กับ business key ตลอดไป**: `pr_number` แก้ไม่ได้หลังสร้าง เลขที่มีช่องว่างติดมา
 * จากการคัดลอกจึงกลายเป็นเลขที่พิมพ์ตามไม่ได้ และเป็นเลขที่ unique constraint มองว่า
 * ไม่ซ้ำกับเลขเดียวกันที่ไม่มีช่องว่าง
 *
 * สองช่องท้าย (ผู้ขายที่เสนอ และหมายเหตุ) ที่ว่างเปล่ากลายเป็น **คีย์ที่หายไปทั้งคีย์**
 * ไม่ใช่สตริงว่างที่ถูกส่งออกไป — `min_length=1` ฝั่งเซิร์ฟเวอร์จะตอบ `400` ให้กับ
 * สตริงว่าง ทั้งที่ผู้ใช้แค่ไม่ได้กรอกช่องที่ไม่บังคับ
 */
export function toCreatePurchaseRequisitionBody(
  fields: CreatePurchaseRequisitionFields,
): CreatePurchaseRequisitionBody | null {
  const prNumber = fields.prNumber.trim();
  const plantCode = fields.plantCode.trim();
  const productCode = fields.productCode.trim();
  const quantity = fields.quantity.trim();
  const uomCode = fields.uomCode.trim();
  const requiredDate = toRequiredDateIso(fields.requiredDate);

  if (
    prNumber === '' ||
    plantCode === '' ||
    productCode === '' ||
    uomCode === '' ||
    requiredDate === null ||
    !isPositiveQuantity(quantity)
  ) {
    return null;
  }

  const suggestedSupplierCode = fields.suggestedSupplierCode.trim();
  const note = fields.note.trim();

  return {
    pr_number: prNumber,
    plant_code: plantCode,
    product_code: productCode,
    quantity,
    uom_code: uomCode,
    required_date: requiredDate,
    ...(suggestedSupplierCode === ''
      ? {}
      : { suggested_supplier_code: suggestedSupplierCode }),
    ...(note === '' ? {} : { note }),
  };
}

/** ค่าที่ฟอร์มหนึ่งรายการถืออยู่ — ดิบ ๆ ตามที่ผู้ใช้พิมพ์ (D-180) */
export interface CreateRequisitionItemFields {
  readonly productCode: string;
  readonly quantity: string;
  readonly uomCode: string;
  readonly requiredDate: string;
  readonly suggestedSupplierCode: string;
  readonly note: string;
}

/** ค่าที่ฟอร์มเปิดหัวกระดาษถืออยู่: โรงงานเดียว หมายเหตุ และหลายรายการ (D-180) */
export interface CreateRequisitionSheetFields {
  readonly plantCode: string;
  readonly remarks: string;
  readonly items: readonly CreateRequisitionItemFields[];
}

export const EMPTY_REQUISITION_ITEM: CreateRequisitionItemFields = {
  productCode: '',
  quantity: '',
  uomCode: '',
  requiredDate: '',
  suggestedSupplierCode: '',
  note: '',
};

export const EMPTY_REQUISITION_SHEET: CreateRequisitionSheetFields = {
  plantCode: '',
  remarks: '',
  items: [EMPTY_REQUISITION_ITEM],
};

/**
 * ประกอบ body ของ `POST /v1/purchase-requisition-headers` — `null` เมื่อยังประกอบไม่ได้
 *
 * ทุกรายการต้องครบ (รหัสสินค้า จำนวนมากกว่าศูนย์ หน่วย วันที่ถูกต้อง) ไม่งั้นทั้งใบยังส่งไม่ได้:
 * เซิร์ฟเวอร์ลงทุกรายการพร้อมกันหรือไม่ลงเลย ปุ่มส่งจึงต้องรอจนทุกแถวพร้อม
 * ช่องไม่บังคับที่ว่างหายไปทั้งคีย์ ไม่ใช่สตริงว่าง
 */
export function toCreatePurchaseRequisitionHeaderBody(
  fields: CreateRequisitionSheetFields,
): CreatePurchaseRequisitionHeaderBody | null {
  const plantCode = fields.plantCode.trim();
  if (plantCode === '' || fields.items.length === 0 || fields.items.length > PR_MAX_ITEMS_PER_SHEET) {
    return null;
  }
  const items: CreatePurchaseRequisitionItemBody[] = [];
  for (const item of fields.items) {
    const productCode = item.productCode.trim();
    const quantity = item.quantity.trim();
    const uomCode = item.uomCode.trim();
    const requiredDate = toRequiredDateIso(item.requiredDate);
    if (productCode === '' || uomCode === '' || requiredDate === null || !isPositiveQuantity(quantity)) {
      return null;
    }
    const supplier = item.suggestedSupplierCode.trim();
    const note = item.note.trim();
    items.push({
      product_code: productCode,
      quantity,
      uom_code: uomCode,
      required_date: requiredDate,
      ...(supplier === '' ? {} : { suggested_supplier_code: supplier }),
      ...(note === '' ? {} : { note }),
    });
  }
  const remarks = fields.remarks.trim();
  return { plant_code: plantCode, ...(remarks === '' ? {} : { remarks }), items };
}

/* ==========================================================================
 * ใบสั่งซื้อ (Purchase Order) — ตรรกะล้วนของหน้าจอ P2-2b (D-085)
 *
 * สามคำถามที่ตอบได้ที่นี่โดยไม่ต้องเรนเดอร์อะไรเลย และผิดแล้วแพงที่สุด:
 *  1. **แถวนี้มีปุ่มอะไรได้บ้าง** (`purchaseOrderLifecycleActionsFor`) — state machine
 *     สี่ขั้นของ contract ทั้งอันอยู่ในฟังก์ชันเดียว
 *  2. **บรรทัดที่ส่งออกไปมีอะไรได้บ้าง** (`toCreatePurchaseOrderBody`) — สองคีย์
 *     เท่านั้น เพราะสินค้า/จำนวน/หน่วยเป็นของใบขอซื้อ
 *  3. **ราคาที่กรอกใช้ได้หรือยัง** (`isValidUnitPrice`) — ตรวจเป็นสตริงล้วน
 *
 * ⚠️ ไฟล์นี้ **ไม่มีฟังก์ชันที่คูณ `quantity × unit_price` เลย** และนั่นเป็นข้อจำกัด
 * โดยเจตนา: `line_amount` เป็นของเซิร์ฟเวอร์ การคูณด้วย IEEE-754 double ที่หน้าจอคือ
 * จุดที่ยอดของเอกสารผูกมัดจะเพี้ยนจากยอดที่เซิร์ฟเวอร์เก็บไว้ — และผู้ใช้จะเชื่อยอดที่
 * เขาเห็น ไม่ใช่ยอดที่ผู้ขายได้รับ
 * ========================================================================== */

export interface PurchaseOrderStatusDescriptor {
  readonly code: string;
  readonly label: string;
  readonly summary: string;
}

/**
 * เจ็ดสถานะของ lifecycle ตามลำดับที่ contract เขียนไว้
 *
 * สองสถานะสุดท้ายก่อนยกเลิก (`PARTIALLY_RECEIVED`, `CLOSED`) ถูกเขียนโดย **งานรับเข้า
 * (P2-3)** ไม่ใช่หน้าจอนี้ — ข้อความของมันจึงต้องบอกว่า "ของมาถึงแล้ว" ซึ่งเป็นเหตุผล
 * ที่ยกเลิกไม่ได้อีก ไม่ใช่แค่แปลชื่อสถานะ
 */
export const PURCHASE_ORDER_STATUS_DESCRIPTORS: readonly PurchaseOrderStatusDescriptor[] =
  [
    {
      code: DRAFT_PO_STATUS,
      label: 'ร่าง (DRAFT)',
      summary: 'ออกใบแล้วแต่ยังไม่มีใครอนุมัติ — ยังไม่ได้ส่งให้ผู้ขายและยังไม่ผูกมัดอะไร',
    },
    {
      code: APPROVED_PO_STATUS,
      label: 'อนุมัติแล้ว (APPROVED)',
      summary: 'ผ่านผู้อนุมัติซึ่งต้องไม่ใช่ผู้ออกใบ (D-010) — รอส่งให้ผู้ขาย',
    },
    {
      code: SENT_PO_STATUS,
      label: 'ส่งให้ผู้ขายแล้ว (SENT)',
      summary: 'เอกสารถึงมือผู้ขายแล้ว รอเขายืนยันรับคำสั่งซื้อ',
    },
    {
      code: ACKNOWLEDGED_PO_STATUS,
      label: 'ผู้ขายยืนยันแล้ว (ACKNOWLEDGED)',
      summary: 'ผู้ขายรับคำสั่งซื้อแล้ว — ขั้นถัดไปคือของมาถึงและงานรับเข้าเป็นผู้ขับ',
    },
    {
      code: PARTIALLY_RECEIVED_PO_STATUS,
      label: 'รับเข้าบางส่วนแล้ว (PARTIALLY_RECEIVED)',
      summary:
        'ของมาถึงแล้วบางส่วน — สถานะนี้เขียนโดยงานรับเข้า (P2-3) ไม่ใช่หน้าจอนี้ และยกเลิกไม่ได้อีก',
    },
    {
      code: CLOSED_PO_STATUS,
      label: 'ปิดใบแล้ว (CLOSED)',
      summary: 'รับของครบแล้วและใบถูกปิด — เขียนโดยงานรับเข้า (P2-3) เช่นกัน',
    },
    {
      code: CANCELLED_PO_STATUS,
      label: 'ยกเลิกแล้ว (CANCELLED)',
      summary:
        'ถอนคำสั่งซื้อก่อนของมาถึง — ใบขอซื้อที่รวมมายังอยู่ที่ CONVERTED_TO_PO การสั่งใหม่ต้องเปิดใบขอซื้อใหม่ (D-085)',
    },
  ];

/** สีของป้ายสถานะ — บอก "ต้องทำอะไรต่อ" ไม่ใช่บอกว่าดีหรือแย่ */
export type PurchaseOrderStatusTone = 'neutral' | 'warning' | 'success' | 'info';

export function purchaseOrderStatusTone(status: string): PurchaseOrderStatusTone {
  switch (status) {
    case DRAFT_PO_STATUS:
      // สีเตือนเพราะมันคือใบที่ **รอคนกด** ไม่ใช่เพราะมีอะไรผิด
      return 'warning';
    case APPROVED_PO_STATUS:
    case ACKNOWLEDGED_PO_STATUS:
      return 'success';
    case SENT_PO_STATUS:
    case PARTIALLY_RECEIVED_PO_STATUS:
    case CLOSED_PO_STATUS:
      return 'info';
    default:
      return 'neutral';
  }
}

/** ชื่อที่อ่านออกของสถานะ — **คืนค่าดิบเมื่อไม่รู้จัก ไม่ใช่ซ่อนแถวนั้น** */
export function purchaseOrderStatusLabel(status: string): string {
  return (
    PURCHASE_ORDER_STATUS_DESCRIPTORS.find((entry) => entry.code === status)?.label ??
    status
  );
}

/**
 * ใบในสถานะนี้มี transition อะไรให้กระทำได้บ้าง — **state machine ของ contract ทั้งอัน
 * อยู่ในฟังก์ชันเดียวนี้**
 *
 * `DRAFT` → อนุมัติ หรือ ยกเลิก
 * `APPROVED` → ส่งให้ผู้ขาย หรือ ยกเลิก
 * `SENT` → บันทึกว่าผู้ขายยืนยัน หรือ ยกเลิก
 * `ACKNOWLEDGED` → **ยกเลิกได้อย่างเดียว** (ขั้นถัดไปเป็นของงานรับเข้า)
 * `PARTIALLY_RECEIVED` / `CLOSED` / `CANCELLED` → **ไม่มีเลย** — สองตัวแรกคือใบที่
 * ของมาถึงแล้วจริง ๆ การยกเลิกจึงไม่ใช่เส้นทางที่มีอยู่ และสถานะที่หน้าจอยังไม่รู้จัก
 * ก็ไม่มีปุ่มเช่นกัน (deny-safe)
 *
 * ⚠️ นี่คือ **UX layer** ไม่ใช่ด่านความปลอดภัย เซิร์ฟเวอร์ตรวจ source status ของทุก
 * คำขอเสมอ สิ่งที่ฟังก์ชันนี้ทำคือไม่เสนอปุ่มที่จะถูกปฏิเสธแน่ ๆ ให้ผู้ใช้กด
 */
export function purchaseOrderLifecycleActionsFor(
  status: string,
): readonly PurchaseOrderTransition[] {
  switch (status) {
    case DRAFT_PO_STATUS:
      return ['approve', 'cancel'];
    case APPROVED_PO_STATUS:
      return ['send', 'cancel'];
    case SENT_PO_STATUS:
      return ['acknowledge', 'cancel'];
    case ACKNOWLEDGED_PO_STATUS:
      return ['cancel'];
    default:
      return [];
  }
}

/** สิทธิ์ที่ปุ่มแต่ละปุ่มต้องการ — ตรงกับด่านจริงฝั่งเซิร์ฟเวอร์ทีละเส้น */
export function purchaseOrderActionPermission(
  transition: PurchaseOrderTransition,
): 'EDIT' | 'APPROVE' {
  return transition === 'approve' ? 'APPROVE' : 'EDIT';
}

/** คำบนปุ่ม */
export function purchaseOrderActionLabel(transition: PurchaseOrderTransition): string {
  switch (transition) {
    case 'approve':
      return 'อนุมัติ';
    case 'send':
      return 'ส่งให้ผู้ขาย';
    case 'acknowledge':
      return 'ผู้ขายยืนยันแล้ว';
    case 'cancel':
      return 'ยกเลิกใบ';
  }
}

/** สถานะที่ transition นั้นพาใบไปถึง — ใช้เขียนข้อความยืนยันผลที่อ่านออก */
export function purchaseOrderActionTargetStatus(
  transition: PurchaseOrderTransition,
): string {
  switch (transition) {
    case 'approve':
      return APPROVED_PO_STATUS;
    case 'send':
      return SENT_PO_STATUS;
    case 'acknowledge':
      return ACKNOWLEDGED_PO_STATUS;
    case 'cancel':
      return CANCELLED_PO_STATUS;
  }
}

/**
 * สามสถานะที่กรอก "วันส่งที่ Supplier ยืนยัน" ได้ (D-163 ข้อ 2) — ตรงกับที่ backend
 * ยอมรับเป๊ะ ๆ (`422 PO_CONFIRMED_DATE_NOT_ALLOWED_IN_STATUS` สำหรับสถานะอื่น)
 */
export const CONFIRMED_DATE_EDITABLE_STATUSES = [
  SENT_PO_STATUS,
  ACKNOWLEDGED_PO_STATUS,
  PARTIALLY_RECEIVED_PO_STATUS,
] as const;

/**
 * ช่องกรอกวันที่ยืนยันแสดงได้ไหม — ต้องทั้งอยู่ในสถานะที่กรอกได้ **และ** ถือสิทธิ์
 * `EDIT` ของ `PROCUREMENT` ตัวเดียวกับที่ปุ่ม transition อื่นใช้อยู่แล้ว (ไม่มีสิทธิ์
 * ชุดใหม่สำหรับฟีเจอร์นี้)
 */
export function canEditConfirmedDate(status: string, permissions: PermissionsState): boolean {
  return (
    (CONFIRMED_DATE_EDITABLE_STATUSES as readonly string[]).includes(status) &&
    can(permissions, PROCUREMENT_MODULE, 'EDIT')
  );
}

/** ข้อความของวันส่งที่ยืนยัน — วันที่ตามที่เซิร์ฟเวอร์ส่งมา หรือ "—" เมื่อยังไม่มี */
export function confirmedDateLabel(value: string | null): string {
  return value ?? '—';
}

/**
 * กรองรายการด้วยคำค้นเดียว — เทียบ **เลขใบสั่งซื้อและรหัสผู้ขาย**
 *
 * สองคอลัมน์นี้คือสิ่งที่คนถืองานอยู่ในมือจะคัดลอกมาค้น: เลขใบคือสิ่งที่ผู้ขายอ้างกลับ
 * มาในอีเมลและใบส่งของ ส่วนรหัสผู้ขายคือวิธีที่เจ้าหน้าที่ดูว่า "เราค้างอะไรกับเจ้านี้
 * อยู่บ้าง" — ต่างจากใบขอซื้อที่ค้นด้วยรหัสสินค้า เพราะใบสั่งซื้อหนึ่งใบมีได้หลายสินค้า
 *
 * กรอง **ฝั่งเบราว์เซอร์จากหน้าที่โหลดมาแล้ว** ไม่ยิงคำขอใหม่ — contract ของ
 * `GET /v1/purchase-orders` ไม่มีพารามิเตอร์ค้นหาอิสระ มีแต่ตัวกรองแบบตรงตัว
 */
export function filterPurchaseOrders(
  items: readonly PurchaseOrder[],
  query: string,
): readonly PurchaseOrder[] {
  const needle = query.trim().toLocaleLowerCase();
  if (needle === '') {
    return items;
  }
  return items.filter(
    (order) =>
      order.po_number.toLocaleLowerCase().includes(needle) ||
      order.supplier_code.toLocaleLowerCase().includes(needle),
  );
}

/**
 * ใบขอซื้อที่ **สั่งซื้อได้จริง** จากชุดที่โหลดมา — `APPROVED` เท่านั้น
 *
 * เซิร์ฟเวอร์กรองให้แล้วผ่าน `status=APPROVED` ในคำขอ ด่านนี้จึงเป็นด่านที่สอง ไม่ใช่
 * ด่านเดียว — และมันคุ้มค่า เพราะใบที่ไม่ใช่ `APPROVED` ที่หลุดเข้ามาในรายการให้เลือก
 * คือบรรทัดที่จะทำให้ **ทั้งใบ** ถูกปฏิเสธ (สร้างใบสั่งซื้อเป็น all-or-nothing) ทั้งที่
 * บรรทัดอื่นถูกต้องหมด
 */
export function orderableRequisitions(
  items: readonly PurchaseRequisition[],
): readonly PurchaseRequisition[] {
  return items.filter((requisition) => requisition.status === APPROVED_PR_STATUS);
}

/**
 * ราคาต่อหน่วยที่กรอกใช้ได้หรือยัง — ตรวจ **เป็นสตริง ไม่แปลงเป็น number แม้แต่ครั้งเดียว**
 *
 * เหตุผลเดียวกับ `isPositiveQuantity()` — `NUMERIC(18, 4)` กว้างกว่าที่ IEEE-754 double
 * เก็บได้ครบ และราคาที่ปัดแล้วคือราคาที่ไม่มีใครตกลงกัน
 *
 * ต่างจาก `isPositiveQuantity()` ตรงที่ **ศูนย์ผ่าน**: contract บังคับแค่
 * `unit_price >= 0` (`400 UNIT_PRICE_MUST_NOT_BE_NEGATIVE` กันเฉพาะค่าติดลบ) และของ
 * แถมที่ราคาศูนย์เป็นรายการที่มีอยู่จริงบนใบสั่งซื้อ การห้ามที่หน้าจอจะห้ามสิ่งที่
 * เซิร์ฟเวอร์ยอมรับ
 *
 * เครื่องหมายลบไม่ผ่าน regex นี้ตั้งแต่ต้น ผู้ใช้จึงรู้ผลทันทีโดยไม่ต้องเดินทางไปกลับ
 */
export function isValidUnitPrice(value: string): boolean {
  return /^\d{1,14}(\.\d{1,4})?$/.test(value.trim());
}

/** ค่าที่หัวใบของฟอร์มสร้างใบสั่งซื้อถืออยู่ — ดิบ ๆ ตามที่ผู้ใช้พิมพ์ */
export interface CreatePurchaseOrderFields {
  readonly poNumber: string;
  readonly plantCode: string;
  readonly supplierCode: string;
  readonly note: string;
}

/**
 * หนึ่งบรรทัดที่ฟอร์มถืออยู่ — ใบขอซื้อที่ถูกติ๊กเลือก พร้อมราคาที่กรอกให้มัน
 *
 * ถือ `prNumber` ไม่ใช่ `prId`: contract ของบรรทัดรับ **business key** และนั่นคือสิ่ง
 * เดียวที่เดินทางออกไป
 */
export interface PurchaseOrderLineDraft {
  readonly prNumber: string;
  readonly unitPrice: string;
}

/**
 * ประกอบ body ของ `POST /v1/purchase-orders` — คืน `null` เมื่อยังประกอบไม่ได้
 *
 * เงื่อนไขที่ต้องครบ: เลขใบ/โรงงาน/ผู้ขายไม่ว่าง **มีอย่างน้อยหนึ่งบรรทัด** และ
 * ทุกบรรทัดมีราคาที่ใช้ได้ — บรรทัดเดียวที่ราคาผิดรูปทำให้ **ทั้งใบ** ประกอบไม่ได้
 * ไม่ใช่ถูกตัดทิ้งเงียบ ๆ: การสร้างใบเป็น all-or-nothing ฝั่งเซิร์ฟเวอร์อยู่แล้ว และ
 * ใบที่ส่งไปโดยขาดบรรทัดที่ผู้ใช้ตั้งใจใส่ คือของที่เขาจะไม่ได้รับโดยไม่มีใครบอก
 *
 * ทุกรหัสถูก `trim()` ด้วยเหตุผลเดียวกับใบขอซื้อ — `po_number` แก้ไม่ได้หลังสร้าง และ
 * เป็นเลขที่ผู้ขายจะอ้างกลับมา
 *
 * `note` ที่ว่างเปล่ากลายเป็น **คีย์ที่หายไปทั้งคีย์** ไม่ใช่สตริงว่างที่ถูกส่งออกไป
 */
export function toCreatePurchaseOrderBody(
  fields: CreatePurchaseOrderFields,
  lines: readonly PurchaseOrderLineDraft[],
): CreatePurchaseOrderBody | null {
  const poNumber = fields.poNumber.trim();
  const plantCode = fields.plantCode.trim();
  const supplierCode = fields.supplierCode.trim();

  if (poNumber === '' || plantCode === '' || supplierCode === '' || lines.length === 0) {
    return null;
  }

  const serializedLines: CreatePurchaseOrderLine[] = [];
  for (const line of lines) {
    const prNumber = line.prNumber.trim();
    const unitPrice = line.unitPrice.trim();
    if (prNumber === '' || !isValidUnitPrice(unitPrice)) {
      return null;
    }
    serializedLines.push({ pr_number: prNumber, unit_price: unitPrice });
  }

  const note = fields.note.trim();

  return {
    po_number: poNumber,
    plant_code: plantCode,
    supplier_code: supplierCode,
    ...(note === '' ? {} : { note }),
    lines: serializedLines,
  };
}
